import { useEffect } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { SelectFormField } from "@/modules/patients/detail/health-information/select-form-field"
import { GynaecologyForm } from "@/modules/patients/schema/gynaecology-form-schema"
import { PregnancyStatusValues } from "@/types/__generated__/union-types"

const regexIntegerNum = /^\d{0,3}$/

const PregnantForm = () => {
  const { t } = useTranslation()
  const form = useFormContext<GynaecologyForm>()
  const {
    setValue,
    formState: { errors: formErrors },
    control,
    trigger,
  } = form

  const pregnancyStatusWatch = useWatch({ control, name: "pregnancyStatus" })
  const menopauseSinceWatch = useWatch({ control, name: "menopauseSince" })
  const currentYear = new Date().getFullYear()

  const isMenopausePastYear = (menopauseSince?: string, currentYear?: number): boolean => {
    if (!menopauseSince || !currentYear) return false
    return currentYear - parseInt(menopauseSince) > 1
  }

  useEffect(() => {
    const checkMenopauseSince = async () => {
      const isFieldValid = await trigger("menopauseSince")
      if (isFieldValid) {
        if (isMenopausePastYear(menopauseSinceWatch, currentYear)) {
          setValue("pregnancyStatus", "NOT_PREGNANT")
          setValue("weeksOfPregnancy", "")
        }
      }
    }

    checkMenopauseSince()
  }, [menopauseSinceWatch, currentYear, trigger, setValue])

  const onWeeksOfPregnancyChange = (value: string) => {
    if (!regexIntegerNum.test(value)) {
      return
    }
    setValue("weeksOfPregnancy", value, { shouldDirty: true, shouldValidate: true })
  }

  if (isMenopausePastYear(menopauseSinceWatch, currentYear)) {
    return null
  }

  return (
    <>
      <div className="col-span-2">
        <SelectFormField
          fieldName="pregnancyStatus"
          label={t("patient.healthInformation.gynaecology.form.currentlyPregnant")}
          options={PregnancyStatusValues.map((option) => ({
            label: t(`patient.healthInformation.gynaecology.form.options.${option}`),
            value: option,
          }))}
        />
      </div>
      {pregnancyStatusWatch === "PREGNANT" && (
        <div className="col-span-2">
          <FormField
            name="weeksOfPregnancy"
            control={control}
            render={({ field: { value } }) => (
              <Input
                error={formErrors.weeksOfPregnancy?.message}
                translateError
                onChange={(e) => {
                  onWeeksOfPregnancyChange(e.target.value)
                }}
                value={value ?? ""}
                label={t("patient.healthInformation.gynaecology.form.weekOfPregnancy")}
                required
                type="text"
                testId="weeks-of-pregnancy"
              />
            )}
          />
        </div>
      )}
    </>
  )
}

export { PregnantForm }
